import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCheck, FiAlertCircle, FiMessageCircle } from 'react-icons/fi';
import Confetti from 'react-confetti';
import api from '../services/api';
import './EnrollmentForm.css';

const EnrollmentForm = ({ isOpen, onClose, onEnrollSuccess }) => {
  const [step, setStep] = useState(1);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    college: '',
    passingYear: new Date().getFullYear(),
    stream: '',
    interest: '',
    otherInterest: ''
  });

  const [errors, setErrors] = useState({});

  const streamOptions = [
    { value: '', label: 'Select Stream' },
    { value: 'CS', label: 'Computer Science (CS)' },
    { value: 'IT', label: 'Information Technology (IT)' },
    { value: 'DSDA', label: 'Data Science & Data Analytics (DSDA)' },
    { value: 'BA', label: 'Arts (BA)' },
    { value: 'BCOM', label: 'Commerce (BCOM) / (BFM)' },
    { value: 'BMMC', label: 'Mass Media & Communication (BMMC)' },
  ];

  const techDomainOptions = [
    { value: '', label: 'Select Domain' },
    { value: 'web', label: 'Web Development' },
    { value: 'app', label: 'App Development' },
    { value: 'backend', label: 'Backend Development' },
    { value: 'api', label: 'API Development' },
    { value: 'data', label: 'Data Analysis' },
    { value: 'ai', label: 'AI Automation' },
    { value: 'other', label: 'Other (Specify)' }
  ];

  const techStreams = ['CS', 'IT', 'DSDA'];

  const validateStep = () => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.phone)) {
        newErrors.phone = 'Invalid phone number (10 digits required)';
      }
      if (!formData.college.trim()) newErrors.college = 'College name is required';
    }

    if (step === 2) {
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.passingYear) newErrors.passingYear = 'Passing year is required';
      if (!formData.stream) newErrors.stream = 'Stream is required';

      if (techStreams.includes(formData.stream)) {
        if (!formData.interest) newErrors.interest = 'Domain interest is required';
        if (formData.interest === 'other' && !formData.otherInterest.trim()) {
          newErrors.otherInterest = 'Please specify your interest';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep()) {
      setIsSubmitting(true);
      try {
        const response = await api.saveEnrollment(formData);

        setRegistrationSuccess(true);
        setRegistrationData(response.data);
        setShowConfetti(true);
        onEnrollSuccess(formData);

        // Auto close after 5 seconds
        // setTimeout(() => {
        //   handleReset();
        //   onClose();
        // }, 5000);
      } catch (error) {
        console.error('Registration failed:', error);
        setErrors({
          submit: error.response?.data?.message || 'Registration failed. Please try again.'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleReset = () => {
    setFormData({
      name: '', email: '', phone: '', address: '', college: '',
      passingYear: new Date().getFullYear(), stream: '', interest: '', otherInterest: ''
    });
    setStep(1);
    setErrors({});
    setRegistrationSuccess(false);
    setRegistrationData(null);
    setShowConfetti(false);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear + i);

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}

      <div className="enrollment-overlay" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="enrollment-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="enrollment-header">
            <h2 className="enrollment-title">
              {registrationSuccess ? '🎉 Registration Successful!' : 'Register Now'}
            </h2>
            <button className="close-button" onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>

          <AnimatePresence mode="wait">
            {registrationSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="success-container"
              >
               

                <div className="success-scrollable-content">
                  <div className="success-icon">
                    <FiCheck size={48} />
                  </div>

                  <p className="success-text">
                    Welcome to DYLORA! Your registration is confirmed. Check your email for details and next steps.
                  </p>

                  <div className="whatsapp-section">
                    <div className="whatsapp-header">
                      <FiMessageCircle className="whatsapp-icon" />
                      <h4>Important Next Step</h4>
                    </div>
                    <p className="whatsapp-text">
                      <strong>Join our WhatsApp group</strong> to complete your enrollment,
                      get payment instructions, and receive program updates:
                    </p>

                    <a
                      href="https://chat.whatsapp.com/EXAMPLE_GROUP_LINK"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whatsapp-button"
                    >
                      Join WhatsApp Group
                    </a>

                    <div className="whatsapp-note">
                      <FiAlertCircle className="note-icon" />
                      <p>Your seat is reserved for 48 hours. Please join the group and complete payment to confirm enrollment.</p>
                    </div>
                  </div>

                  <div className="success-details">
                    <h4 className="details-title">Registration Details</h4>
                    <div className="detail-item">
                      <strong>Name:</strong> {registrationData?.name || formData.name}
                    </div>
                    <div className="detail-item">
                      <strong>Email:</strong> {registrationData?.email || formData.email}
                    </div>
                    <div className="detail-item">
                      <strong>Phone:</strong> {registrationData?.phone || formData.phone}
                    </div>
                    <div className="detail-item">
                      <strong>Program:</strong> DYLORA 1+1 Internship Program
                    </div>
                    <div className="detail-item">
                      <strong>Program Fee:</strong> ₹599
                    </div>
                    <div className="detail-item">
                      <strong>Registration ID:</strong> {registrationData?.id?.substring(0, 8) || 'REG-' + Date.now().toString().slice(-8)}
                    </div>
                    <div className="detail-item">
                      <strong>Stream:</strong> {registrationData?.stream || formData.stream}
                    </div>
                    {registrationData?.interest && (
                      <div className="detail-item">
                        <strong>Domain Interest:</strong> {registrationData?.interest}
                      </div>
                    )}
                    <div className="detail-item">
                      <strong>Registration Date:</strong> {new Date().toLocaleDateString()}
                    </div>
                  </div>

                  <div className="important-notes">
                    <h4 className="notes-title">Important Notes</h4>
                    <div className="note-item">
                      <FiAlertCircle className="note-icon-small" />
                      <span>Check your email (including spam folder) for confirmation</span>
                    </div>
                    <div className="note-item">
                      <FiAlertCircle className="note-icon-small" />
                      <span>WhatsApp group link is also sent to your email</span>
                    </div>
                    <div className="note-item">
                      <FiAlertCircle className="note-icon-small" />
                      <span>Complete payment within 48 hours to secure your seat</span>
                    </div>
                    <div className="note-item">
                      <FiAlertCircle className="note-icon-small" />
                      <span>Keep your Registration ID for future reference</span>
                    </div>
                  </div>

                  <div className="next-steps">
                    <h4 className="steps-title">Next Steps</h4>
                    <div className="step-item">
                      <div className="step-number">1</div>
                      <div className="step-content">Join WhatsApp group using link above</div>
                    </div>
                    <div className="step-item">
                      <div className="step-number">2</div>
                      <div className="step-content">Complete payment as instructed in group</div>
                    </div>
                    <div className="step-item">
                      <div className="step-number">3</div>
                      <div className="step-content">Receive onboarding email with program details</div>
                    </div>
                    <div className="step-item">
                      <div className="step-number">4</div>
                      <div className="step-content">Attend orientation session</div>
                    </div>
                  </div>
                </div>

                <div className="success-actions">
                  <button
                    onClick={() => {
                      handleReset();
                      onClose();
                    }}
                    className="close-modal-button black-btn"
                  >
                    Close & Continue
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="form-container"
              >
                <div className="progress-container">
                  <div className="progress-steps">
                    {[1, 2, 3].map((stepNum) => (
                      <div key={stepNum} className="step-container">
                        <div className={`step-circle ${step >= stepNum ? 'active' : ''}`}>
                          {stepNum}
                        </div>
                        <div className="step-label">
                          {stepNum === 1 ? 'Personal' : stepNum === 2 ? 'Academic' : 'Confirm'}
                        </div>
                      </div>
                    ))}
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${((step - 1) / 2) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-content">
                  {step === 1 && (
                    <div className="form-step">
                      <h3 className="step-title">Personal Information</h3>
                      <div className="form-group">
                        <label className="label">Full Name *</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className={`input ${errors.name ? 'error' : ''}`}
                          placeholder="Enter your full name"
                        />
                        {errors.name && <div className="error-msg">{errors.name}</div>}
                      </div>

                      <div className="form-group">
                        <label className="label">Email Address *</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className={`input ${errors.email ? 'error' : ''}`}
                          placeholder="Enter your email"
                        />
                        {errors.email && <div className="error-msg">{errors.email}</div>}
                      </div>

                      <div className="form-group">
                        <label className="label">Phone Number *</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className={`input ${errors.phone ? 'error' : ''}`}
                          placeholder="10-digit mobile number"
                        />
                        {errors.phone && <div className="error-msg">{errors.phone}</div>}
                      </div>

                      <div className="form-group">
                        <label className="label">College/University *</label>
                        <input
                          type="text"
                          value={formData.college}
                          onChange={(e) => setFormData({ ...formData, college: e.target.value })}
                          className={`input ${errors.college ? 'error' : ''}`}
                          placeholder="Enter your college name"
                        />
                        {errors.college && <div className="error-msg">{errors.college}</div>}
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="form-step">
                      <h3 className="step-title">Academic Details</h3>

                      <div className="form-group">
                        <label className="label">Complete Address *</label>
                        <textarea
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          className={`textarea ${errors.address ? 'error' : ''}`}
                          placeholder="Enter your complete address"
                          rows="3"
                        />
                        {errors.address && <div className="error-msg">{errors.address}</div>}
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="label">Passing Year *</label>
                          <select
                            value={formData.passingYear}
                            onChange={(e) => setFormData({ ...formData, passingYear: parseInt(e.target.value) })}
                            className={`select ${errors.passingYear ? 'error' : ''}`}
                          >
                            <option value="">Select Year</option>
                            {yearOptions.map((year) => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                          {errors.passingYear && <div className="error-msg">{errors.passingYear}</div>}
                        </div>

                        <div className="form-group">
                          <label className="label">Stream *</label>
                          <select
                            value={formData.stream}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                stream: e.target.value,
                                interest: '',
                                otherInterest: ''
                              });
                            }}
                            className={`select ${errors.stream ? 'error' : ''}`}
                          >
                            {streamOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {errors.stream && <div className="error-msg">{errors.stream}</div>}
                        </div>
                      </div>

                      {techStreams.includes(formData.stream) && (
                        <>
                          <div className="form-group">
                            <label className="label">Interested In (Tech Domain) *</label>
                            <select
                              value={formData.interest}
                              onChange={(e) => setFormData({
                                ...formData,
                                interest: e.target.value,
                                otherInterest: e.target.value === 'other' ? formData.otherInterest : ''
                              })}
                              className={`select ${errors.interest ? 'error' : ''}`}
                            >
                              {techDomainOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {errors.interest && <div className="error-msg">{errors.interest}</div>}
                          </div>

                          {formData.interest === 'other' && (
                            <div className="form-group">
                              <label className="label">Specify Your Interest *</label>
                              <input
                                type="text"
                                value={formData.otherInterest}
                                onChange={(e) => setFormData({ ...formData, otherInterest: e.target.value })}
                                className={`input ${errors.otherInterest ? 'error' : ''}`}
                                placeholder="Enter your specific interest"
                              />
                              {errors.otherInterest && <div className="error-msg">{errors.otherInterest}</div>}
                            </div>
                          )}
                        </>
                      )}

                      {formData.stream && !techStreams.includes(formData.stream) && (
                        <div className="info-box">
                          <FiAlertCircle className="info-icon" />
                          <div>
                            <strong>Note for Non-Tech Students:</strong>
                            <p className="info-text">
                              You will receive comprehensive training in all non-tech areas including
                              Soft Skills, Presentations, MS Office, Digital Marketing, and more.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {step === 3 && (
                    <div className="form-step">
                      <h3 className="step-title">Registration Confirmation</h3>

                      <div className="summary-card">
                        <h4 className="summary-title">Registration Summary</h4>

                        <div className="summary-grid">
                          <div className="summary-item">
                            <span className="summary-label">Name:</span>
                            <span className="summary-value">{formData.name}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Email:</span>
                            <span className="summary-value">{formData.email}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Phone:</span>
                            <span className="summary-value">{formData.phone}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">College:</span>
                            <span className="summary-value">{formData.college}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Stream:</span>
                            <span className="summary-value">
                              {streamOptions.find(s => s.value === formData.stream)?.label}
                            </span>
                          </div>
                          {formData.interest && (
                            <div className="summary-item">
                              <span className="summary-label">Domain:</span>
                              <span className="summary-value">
                                {techDomainOptions.find(d => d.value === formData.interest)?.label}
                                {formData.interest === 'other' && ` - ${formData.otherInterest}`}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="payment-summary">
                          <div className="payment-item">
                            <span className="payment-label">Program Fee:</span>
                            <span className="payment-value">₹599</span>
                          </div>
                          <div className="payment-note">
                            <FiAlertCircle className="note-icon" />
                            <p className="note-text">
                              After registration, you'll receive an email with a WhatsApp group link.
                              Join the group to complete payment and get program details.
                            </p>
                          </div>
                        </div>
                      </div>

                      {errors.submit && <div className="submit-error">{errors.submit}</div>}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  {step > 1 && !registrationSuccess && (
                    <button
                      onClick={handlePrev}
                      className="prev-button black-btn-outline"
                      disabled={isSubmitting}
                    >
                      Previous
                    </button>
                  )}

                  {step < 3 && !registrationSuccess && (
                    <button
                      onClick={handleNext}
                      className="next-button black-btn"
                      disabled={isSubmitting}
                    >
                      Next Step
                    </button>
                  )}

                  {step === 3 && !registrationSuccess && (
                    <button
                      onClick={handleSubmit}
                      className="submit-button black-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Registering...' : 'Complete Registration'}
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </>
  );
};

export default EnrollmentForm;